/** OTO-21: result of diffing a render pool's keys against the newly visible
 *  key set for one frame. */
export interface PoolDiff<K> {
  toAdd: K[]; // in visible, not in pool
  toUpdate: K[]; // in both
  toRemove: K[]; // in pool, not in visible
}

/** Pure set-diff between the keys currently in a render pool and the newly
 *  computed visible-key set. No PIXI imports, no side effects. */
export function diffPool<K>(poolKeys: ReadonlySet<K>, visibleKeys: ReadonlySet<K>): PoolDiff<K> {
  const toAdd: K[] = [];
  const toUpdate: K[] = [];
  const toRemove: K[] = [];

  for (const key of visibleKeys) {
    if (poolKeys.has(key)) {
      toUpdate.push(key);
    } else {
      toAdd.push(key);
    }
  }

  for (const key of poolKeys) {
    if (!visibleKeys.has(key)) {
      toRemove.push(key);
    }
  }

  return { toAdd, toUpdate, toRemove };
}

/** OTO-21 Gate B fix: cached per-note visual state used to decide whether a
 *  pooled note entry's bar geometry/label needs to be redrawn this frame. */
export interface NoteVisualCache {
  pitch: number;
  duration: number;
  color: number;
  alpha: number;
}

/** Pure: true if `next` differs from `cached` in pitch, duration, color, or
 *  alpha. `WaterfallDisplay` previously redrew a pooled note's bar geometry
 *  only on a color/alpha transition, so a note whose pitch/duration changed
 *  under the same pooled id (color/alpha unchanged) kept a stale bar
 *  X-position/height/label. */
export function notePoolEntryStale(cached: NoteVisualCache, next: NoteVisualCache): boolean {
  return (
    cached.pitch !== next.pitch ||
    cached.duration !== next.duration ||
    cached.color !== next.color ||
    cached.alpha !== next.alpha
  );
}
